import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import CameraDetail from './components/devices/CameraDetail';
import AlertDetail from './components/monitoring/AlertDetail'
import VoicePrompt from './components/monitoring/VoicePrompts';
import DeviceList from './components/devices/DeviceList';
import DeviceDetail from './components/devices/DeviceDetail';
import DeviceOrder from './components/devices/DeviceOrder';
import LoginPage from './components/auth/LoginPage';
import Registration from './components/auth/Registration'
import UserManagement from './components/administration/UserManagement'
import DeviceConfig from './components/devices/DeviceSettings';
import DroneWaypoints from './components/devices/DroneWaypoints';
import { ToastContainer } from 'react-toastify';
import RoleManagement from './components/administration/RoleManagement';
import DroneRunway from './components/devices/DroneRunway';
import ConstructionSurveyPage from './components/survey/ConstructionSurveyPage';
import ForeignObjectDetectionStream from './components/monitoring/ForeignObjectDetectionStream';
import ForeignObjectAlerts from './components/monitoring/ForeignObjectAlerts';
import { ProtectedRoute } from "./components/shared/ProtectedRoute";
import { DomainRoute } from "./components/shared/DomainRoute";
import { RequireAccess } from "./components/shared/RequireAccess";
import { ROLES } from "./utils/helpers";
import { isFeatureEnabled } from "./config/features";
import { hasValidAuth, isBootstrapAdmin, hasDomain } from "./utils/authStorage";
import AlertDetailWrapper from "./components/monitoring/AlertDetailWrapper";
import DashboardPage from "./components/monitoring/DashboardPage";
import AlertsPage from "./components/monitoring/AlertsPage";
import { AlertWebSocketProvider, DeviceWebSocketProvider } from "./contexts/WebSocketContext";
import { ActiveAlertsProvider } from "./contexts/ActiveAlertsContext";
import { ModuleProvider } from "./contexts/ModuleContext";

// Construction Module Components
import ProjectsPage from "./components/construction/ProjectsPage";
import SiteVisualsPage from "./components/construction/SiteVisualsPage";
import AerialDataPage from "./components/construction/AerialDataPage";
import EarthworkPage from "./components/construction/EarthworkPage";
import StockpilesPage from "./components/construction/StockpilesPage";
import MmmPage from "./components/construction/MmmPage";
import SiteObservationsPage from "./components/construction/SiteObservationsPage";
import ProgressPage from "./components/construction/ProgressPage";
import ReportsPage from "./components/construction/ReportsPage";
import ProjectSettingsPage from "./components/construction/ProjectSettingsPage";
import TenantManagement from "./components/administration/TenantManagement";


function App() {
  return (

    <BrowserRouter>
      <ModuleProvider>
        <AlertWebSocketProvider>
          <DeviceWebSocketProvider>
            <ActiveAlertsProvider>
              <Routes>
                <Route
                  path="/"
                  element={
                    hasValidAuth()
                      ? isBootstrapAdmin()
                        ? <Navigate to="/tenant-management" replace />
                        : hasDomain("construction")
                          ? <Navigate to="/construction/projects" replace />
                          : <Navigate to="/dashboard" replace />
                      : <Navigate to="/login" replace />
                  }
                />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/registration" element={<Registration />} />

                <Route
                  path="/camera/:id"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <CameraDetail />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/alerts/:alertId"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <AlertDetail />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/voice-prompt"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <VoicePrompt />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/device-list"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <DeviceList />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/device/:deviceId"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <DeviceDetail />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/device-order"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <DeviceOrder />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/drone-runway"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <DroneRunway />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/construction-survey"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <ConstructionSurveyPage />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/foreign-object-detection-stream/:flightId"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <ForeignObjectDetectionStream />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/foreign-object-alerts/:alertId"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <ForeignObjectAlerts />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/device/configuration/:deviceId"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <DeviceConfig />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/device/waypoints/:deviceId"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <DroneWaypoints />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tenant-management"
                  element={
                    <ProtectedRoute>
                      <RequireAccess bootstrapOnly>
                        <TenantManagement />
                      </RequireAccess>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/user-management"
                  element={
                    <ProtectedRoute>
                      <UserManagement />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <DashboardPage />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/role-management"
                  element={
                    <ProtectedRoute>
                      <RequireAccess roles={ROLES.ADMIN_ONLY}>
                        <RoleManagement />
                      </RequireAccess>
                    </ProtectedRoute>
                  }
                />
                <Route path="/alert-details" element={<AlertDetailWrapper />} />
                <Route
                  path="/alerts-page"
                  element={
                    <ProtectedRoute>
                      <DomainRoute domain="surveillance">
                        <AlertsPage />
                      </DomainRoute>
                    </ProtectedRoute>
                  }
                />

                {/* Construction Routes */}
                <Route path="/construction/projects" element={<ProtectedRoute><DomainRoute domain="construction"><ProjectsPage /></DomainRoute></ProtectedRoute>} />
                <Route path="/construction/visuals" element={<ProtectedRoute><DomainRoute domain="construction"><SiteVisualsPage /></DomainRoute></ProtectedRoute>} />
                <Route path="/construction/aerial" element={isFeatureEnabled('PAGE_AERIAL_DATA') ? <ProtectedRoute><DomainRoute domain="construction"><AerialDataPage /></DomainRoute></ProtectedRoute> : <Navigate to="/construction/projects" replace />} />
                <Route path="/construction/earthwork" element={<ProtectedRoute><DomainRoute domain="construction"><EarthworkPage /></DomainRoute></ProtectedRoute>} />
                <Route path="/construction/stockpiles" element={<ProtectedRoute><DomainRoute domain="construction"><StockpilesPage /></DomainRoute></ProtectedRoute>} />
                <Route path="/construction/mmm" element={<ProtectedRoute><DomainRoute domain="construction"><MmmPage /></DomainRoute></ProtectedRoute>} />
                <Route path="/construction/observations" element={<ProtectedRoute><DomainRoute domain="construction"><SiteObservationsPage /></DomainRoute></ProtectedRoute>} />
                <Route path="/construction/progress" element={isFeatureEnabled('PAGE_PROGRESS') ? <ProtectedRoute><DomainRoute domain="construction"><ProgressPage /></DomainRoute></ProtectedRoute> : <Navigate to="/construction/projects" replace />} />
                <Route path="/construction/reports" element={isFeatureEnabled('PAGE_REPORTS') ? <ProtectedRoute><DomainRoute domain="construction"><ReportsPage /></DomainRoute></ProtectedRoute> : <Navigate to="/construction/projects" replace />} />
                <Route path="/construction/survey" element={isFeatureEnabled('PAGE_SURVEY') ? <ProtectedRoute><DomainRoute domain="construction"><ConstructionSurveyPage /></DomainRoute></ProtectedRoute> : <Navigate to="/construction/projects" replace />} />
                <Route path="/construction/user-management" element={<ProtectedRoute><RequireAccess roles={ROLES.ADMIN_ONLY}><UserManagement /></RequireAccess></ProtectedRoute>} />
                <Route path="/construction/settings" element={<ProtectedRoute><DomainRoute domain="construction"><RequireAccess roles={ROLES.ADMIN_ONLY}><ProjectSettingsPage /></RequireAccess></DomainRoute></ProtectedRoute>} />

                {/* Legacy site-visuals routes → construction module */}
                <Route path="/projects" element={<Navigate to="/construction/projects" replace />} />
                <Route path="/site-visuals" element={<Navigate to="/construction/visuals" replace />} />

              </Routes>
            </ActiveAlertsProvider>
          </DeviceWebSocketProvider>
        </AlertWebSocketProvider>
      </ModuleProvider>
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </BrowserRouter>
  );
}

export default App;